import axios, { AxiosError, AxiosRequestConfig } from 'axios';

import { HttpError, NetworkError } from '@/lib/exception';

import { DeletePath, GetPath, PatchPath, PostPath, PutPath } from './apiPath';

// NOTE columnを使っている経緯、、使わなくなったらcolumnは消す。
// https://github.com/tacoms-inc-dev/camel-dashboard-react-frontend/pull/640
type AxiosErrorData = { message?: string; column?: string };

/** postFormData の戻り値。status と body をそのまま呼び出し側へ渡します。 */
export type FormDataResponse<Res> = { status: number; data: Res };

/**
 * 送信中のリクエストを中断するための受け口です。
 *
 * axios 0.21 は AbortSignal に未対応で CancelToken しか持たないため、
 * 呼び出し側に axios の型を漏らさないよう「中断関数を受け取るコールバック」
 * という形にしています。
 */
export type CancelableRequestOptions = {
  onCancel?: (abort: () => void) => void;
};

export interface RestClientInterface {
  get: <Res>(path: GetPath) => Promise<Res>;
  /**
   * multipart/form-data を送信し、HTTP status と body をそのまま返します。
   *
   * 共有の handleAxiosError を意図的に通していません。あちらは 403 を 401 に
   * 潰し、413/422 のケースを持たないため、呼び出し側が status と
   * body の error.code を失ってしまうためです。
   */
  postFormData: <Res>(
    path: PostPath,
    form: FormData,
    options?: CancelableRequestOptions
  ) => Promise<FormDataResponse<Res>>;
  post: <Req, Res>(path: PostPath, body?: Req) => Promise<Res>;
  patch: <Req, Res>(path: PatchPath, body: Req) => Promise<Res>;
  put: <Req, Res>(path: PutPath, body: Req) => Promise<Res>;
  delete: <Res>(path: DeletePath) => Promise<Res>;
}

export class RestClient implements RestClientInterface {
  private static instance: RestClient;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  public static getInstance(): RestClient {
    if (!RestClient.instance) {
      RestClient.instance = new RestClient();
    }
    return RestClient.instance;
  }

  async get<Res>(path: GetPath): Promise<Res> {
    return await axios
      .get<Res>(path, this.requestConfig)
      .catch(this.errorHandling)
      .then((res) => res.data);
  }

  async post<Req, Res>(path: PostPath, body?: Req): Promise<Res> {
    return await axios
      .post<Res>(path, body, this.requestConfig)
      .catch(this.errorHandling)
      .then((res) => res.data);
  }

  async postFormData<Res>(
    path: PostPath,
    form: FormData,
    options?: CancelableRequestOptions
  ): Promise<FormDataResponse<Res>> {
    const source = axios.CancelToken.source();
    options?.onCancel?.(() => source.cancel('superseded by a newer request'));

    // Content-Type は指定しません。axios が FormData を検出してヘッダーを外し、
    // ブラウザが multipart の boundary を付与します。
    const res = await axios.post<Res>(path, form, {
      withCredentials: true,
      validateStatus: () => true,
      cancelToken: source.token,
    });
    return { status: res.status, data: res.data };
  }

  async patch<Req, Res>(path: PatchPath, body: Req): Promise<Res> {
    return await axios
      .patch<Res>(path, body, this.requestConfig)
      .catch(this.errorHandling)
      .then((res) => res.data);
  }

  async put<Req, Res>(path: PutPath, body: Req): Promise<Res> {
    return await axios
      .put<Res>(path, body, this.requestConfig)
      .catch(this.errorHandling)
      .then((res) => res.data);
  }

  async delete<Res>(path: DeletePath): Promise<Res> {
    return await axios
      .delete<Res>(path, this.requestConfig)
      .catch(this.errorHandling)
      .then((res) => res.data);
  }

  private get requestConfig(): AxiosRequestConfig {
    return {
      /** @NOTE タブレットとダッシュボードの二つのapiを見に行くためbaseURLをコメントアウト **/
      // baseURL: API_ORIGIN,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    };
  }

  private errorHandling(error: AxiosError<AxiosErrorData>): never {
    return handleAxiosError(error);
  }
}

export const handleAxiosError = (error: AxiosError<AxiosErrorData>): never => {
  if (error.response) {
    const messege = error.response.data?.message ?? error.message;
    switch (error.response.status) {
      case 400:
        // 現時点で独自エラー型は400でしか使われていないが、他のところでもこのメッセージを使われるようになったらこちらを調整する。
        throw new HttpError(400, messege);
      case 401:
        throw new HttpError(401, messege);
      case 403:
        throw new HttpError(401, messege);
      case 404:
        throw new HttpError(404, messege);
      case 409:
        // NOTE columnを使っている経緯、、使わなくなったらcolumnは消す。
        // https://github.com/tacoms-inc-dev/camel-dashboard-react-frontend/pull/640
        // NOTE 409 はレスポンスボディに競合時の状態 (current_status 等) が含まれることがあるため、
        //      呼び出し側がそれを参照できるようボディごと HttpError に持たせる。
        const column = error.response.data?.column;
        if (column !== undefined) {
          throw new HttpError(409, column, error.response.data);
        } else {
          throw new HttpError(409, messege, error.response.data);
        }
      case 425:
        throw new HttpError(425, messege);
      case 500:
        throw new HttpError(500, messege);
      default:
        throw new Error(messege);
    }
  } else if (error.request) {
    throw new NetworkError(error.message);
  } else {
    throw new Error(error.message);
  }
};

export const getClient = (): RestClientInterface => RestClient.getInstance();
